import type { ReactNode } from 'react';

import { RADIUS, Squircle } from '@/shared/ui';

import styles from './SeasonFinalModal.module.scss';

export type SeasonFinalVariant = 'win' | 'over';

interface SeasonFinalModalProps {
  variant: SeasonFinalVariant;
  onClose: () => void;
}

const CONTENT: Record<SeasonFinalVariant, { title: string; body: ReactNode; caption: string }> = {
  // Победа в Финальном розыгрыше сезона (узел 438:53409/438:53111).
  win: {
    title: 'Победа!',
    body: (
      <>
        Поздравляем с победой, главный приз твой. Пакуй чемоданы, и бери
        побольше футболок, там сейчас жарко. Viva Barcelona!
      </>
    ),
    caption: 'Свяжемся с тобой в течение 7 дней и расскажем как забрать приз',
  },
  // Отбивка конца игры для всех остальных (узел 438:53721/438:53423).
  over: {
    title: 'Вот и всё',
    body: (
      <>
        Игра подошла к концу, спасибо, что был её участником! Но наш канал
        и чат остаются открытыми 24/7, всегда ждём тебя!
      </>
    ),
    // Опечатка «роызырышей» в исходном тексте макета — «розыгрышей».
    caption: 'Впереди много активностей, розыгрышей и призов',
  },
};

/**
 * Финал сезона (узлы 438:53111 «победа» / 438:53423 «игра закончилась»).
 *
 * Общие для обоих вариантов шейп-подложка и картинка (кубок/флаг/мяч) —
 * пиксельно идентичны в обоих узлах макета (тот же экспорт, тот же размер
 * и позиция), отличается только текст.
 *
 * Какой вариант показать и когда, решает `SeasonFinalModalHost` по итогу
 * `SEASON_FINAL` из `GET /results/pending`. Сама модалка про источник
 * данных не знает — ей приходит только `variant`.
 */
export function SeasonFinalModal({ variant, onClose }: SeasonFinalModalProps) {
  const content = CONTENT[variant];

  return (
    <div className={styles.sheet} data-testid={`season-final-modal-${variant}`}>
      <div className={styles.shapeBg}>
        <img className={styles.shapeBgImage} src="/images/season-final/shape-bg.svg" alt="" aria-hidden />
      </div>

      <button type="button" className={styles.close} aria-label="Закрыть" onClick={onClose}>
        <img className={styles.closeIcon} src="/images/matches/close-light.svg" alt="" aria-hidden />
      </button>

      <div className={styles.hero}>
        <div className={styles.flagWrap}>
          <img className={styles.flagImage} src="/images/season-final/flag.png" alt="" aria-hidden />
        </div>
        <img className={styles.ball} src="/images/season-final/ball.png" alt="" aria-hidden />
        <img className={styles.trophy} src="/images/season-final/trophy.png" alt="" aria-hidden />
      </div>

      <div className={styles.content}>
        <div className={styles.textScore}>
          <div className={styles.text}>
            <h2 className={styles.title}>{content.title}</h2>
            <p className={styles.body}>{content.body}</p>
          </div>
          <p className={styles.caption}>{content.caption}</p>
        </div>

        <Squircle asChild cornerRadius={RADIUS.xl}>
          <button
            type="button"
            className={styles.cta}
            onClick={onClose}
            data-testid={`season-final-modal-${variant}-cta`}
          >
            Отлично!
          </button>
        </Squircle>
      </div>
    </div>
  );
}
